import * as React from "react";
import { cn } from "../utils/cn";
import { Text } from "../primitives/typography";

/* ----------------------------- PageHeaderRoot ----------------------------- */

export interface PageHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(({ className, children, ...props }, ref) => (
    <div
        ref={ref}
        className={cn("bg-card border-border sticky top-12 z-30 flex flex-col gap-3 border-b px-6 py-4", className)}
        {...props}
    >
        {children}
    </div>
));
PageHeader.displayName = "PageHeader";

/* ---------------------------- PageHeaderBreadcrumb ------------------------ */

export interface PageHeaderBreadcrumbProps extends React.HTMLAttributes<HTMLDivElement> {}

const PageHeaderBreadcrumb = React.forwardRef<HTMLDivElement, PageHeaderBreadcrumbProps>(
    ({ className, children, ...props }, ref) => (
        <div ref={ref} className={cn("", className)} {...props}>
            {children}
        </div>
    ),
);
PageHeaderBreadcrumb.displayName = "PageHeaderBreadcrumb";

/* ------------------------------ PageHeaderRow ----------------------------- */

export interface PageHeaderRowProps extends React.HTMLAttributes<HTMLDivElement> {}

const PageHeaderRow = React.forwardRef<HTMLDivElement, PageHeaderRowProps>(({ className, children, ...props }, ref) => (
    <div
        ref={ref}
        className={cn("flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between", className)}
        {...props}
    >
        {children}
    </div>
));
PageHeaderRow.displayName = "PageHeaderRow";

/* ----------------------------- PageHeaderIdentity ------------------------- */

export interface PageHeaderIdentityProps extends React.HTMLAttributes<HTMLDivElement> {}

const PageHeaderIdentity = React.forwardRef<HTMLDivElement, PageHeaderIdentityProps>(
    ({ className, children, ...props }, ref) => (
        <div ref={ref} className={cn("flex items-center gap-4", className)} {...props}>
            {children}
        </div>
    ),
);
PageHeaderIdentity.displayName = "PageHeaderIdentity";

/* ------------------------------ PageHeaderInfo ---------------------------- */

export interface PageHeaderInfoProps extends React.HTMLAttributes<HTMLDivElement> {}

const PageHeaderInfo = React.forwardRef<HTMLDivElement, PageHeaderInfoProps>(
    ({ className, children, ...props }, ref) => (
        <div ref={ref} className={cn("flex min-w-0 flex-col gap-1", className)} {...props}>
            {children}
        </div>
    ),
);
PageHeaderInfo.displayName = "PageHeaderInfo";

/* ---------------------------- PageHeaderTitleRow -------------------------- */

export interface PageHeaderTitleRowProps extends React.HTMLAttributes<HTMLDivElement> {}

const PageHeaderTitleRow = React.forwardRef<HTMLDivElement, PageHeaderTitleRowProps>(
    ({ className, children, ...props }, ref) => (
        <div ref={ref} className={cn("flex flex-wrap items-center gap-2", className)} {...props}>
            {children}
        </div>
    ),
);
PageHeaderTitleRow.displayName = "PageHeaderTitleRow";

/* ------------------------------ PageHeaderMeta ---------------------------- */

export interface PageHeaderMetaProps extends React.HTMLAttributes<HTMLDivElement> {}

const PageHeaderMeta = React.forwardRef<HTMLDivElement, PageHeaderMetaProps>(
    ({ className, children, ...props }, ref) => (
        <div ref={ref} className={cn("flex flex-wrap items-center gap-2", className)} {...props}>
            {children}
        </div>
    ),
);
PageHeaderMeta.displayName = "PageHeaderMeta";

/* --------------------------- PageHeaderMetaItem --------------------------- */

export interface PageHeaderMetaItemProps extends React.HTMLAttributes<HTMLSpanElement> {}

const PageHeaderMetaItem = React.forwardRef<HTMLSpanElement, PageHeaderMetaItemProps>(
    ({ className, children, ...props }, ref) => (
        <Text
            as="span"
            size="xs"
            muted
            ref={ref as React.Ref<HTMLParagraphElement>}
            className={cn(className)}
            {...(props as React.HTMLAttributes<HTMLParagraphElement>)}
        >
            {children}
        </Text>
    ),
);
PageHeaderMetaItem.displayName = "PageHeaderMetaItem";

/* --------------------------- PageHeaderMetaDot ---------------------------- */

const PageHeaderMetaDot = ({ className }: { className?: string }) => (
    <span className={cn("text-muted-foreground/50 text-xs select-none", className)} aria-hidden>
        ·
    </span>
);
PageHeaderMetaDot.displayName = "PageHeaderMetaDot";

/* ----------------------------- PageHeaderActions -------------------------- */

export interface PageHeaderActionsProps extends React.HTMLAttributes<HTMLDivElement> {}

const PageHeaderActions = React.forwardRef<HTMLDivElement, PageHeaderActionsProps>(
    ({ className, children, ...props }, ref) => (
        <div ref={ref} className={cn("flex shrink-0 items-center gap-2", className)} {...props}>
            {children}
        </div>
    ),
);
PageHeaderActions.displayName = "PageHeaderActions";

export {
    PageHeader,
    PageHeaderBreadcrumb,
    PageHeaderRow,
    PageHeaderIdentity,
    PageHeaderInfo,
    PageHeaderTitleRow,
    PageHeaderMeta,
    PageHeaderMetaItem,
    PageHeaderMetaDot,
    PageHeaderActions,
};
